import { useState } from "react";
import type {
  DirectPost,
  GigSource,
  GigSourcesView,
  PartnerFeed,
  RemindersStatus,
} from "../../api/gigAdmin";
import type { BoardTrack } from "../../api/types";
import { Badge, KpiCard } from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { Banner } from "../../components/Banner";
import { safeHref } from "../../lib/format";

export const BOARD_TRACKS: [BoardTrack, string][] = [
  ["uiux", "UI/UX Design"],
  ["graphic", "Graphic Design"],
  ["video", "Video Production & Editing"],
  ["digital", "Digital Marketing"],
  ["transferable", "Transferable skills"],
];
const trackLabel = (k: string) => BOARD_TRACKS.find(([v]) => v === k)?.[1] ?? k;

const HTTPS = /^https:\/\/\S+$/;

const when = (t?: string | null) =>
  t
    ? new Date(t).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "never";

const inputClass =
  "h-10 w-full rounded-[10px] border-[1.5px] border-border bg-surface px-3 text-[14px] focus:border-accent focus:outline-none";
const smallButton =
  "h-9 rounded-[9px] border-[1.5px] px-3 text-[13px] font-bold disabled:opacity-50";
const primary = `${smallButton} border-accent bg-accent text-on-accent hover:bg-accent-pressed`;
const ghost = `${smallButton} border-border bg-surface text-text hover:border-accent hover:text-accent`;

export interface GigSourcesScreenProps {
  view: GigSourcesView;
  reminders: RemindersStatus | null;
  remindersError: string | null;
  /** SuperAdmin and GigAdmin; MERL reads only. */
  canManage: boolean;
  /** A refresh was asked for here (or one is running on the server). */
  refreshing: boolean;
  /** The source being refreshed on its own, or "all". */
  refreshingId: string | null;
  refreshError: string | null;
  saving: "feeds" | "posts" | null;
  saveError: { list: "feeds" | "posts"; message: string } | null;
  testResult: { tone: "good" | "bad"; message: string } | null;
  testing: boolean;
  onRefresh: (sourceId?: string) => void;
  onSaveFeeds: (feeds: PartnerFeed[]) => Promise<boolean>;
  onSavePosts: (posts: DirectPost[]) => Promise<boolean>;
  onTestReminder: (email: string) => void;
}

function Panel({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[14px] border border-border bg-surface p-5">
      <h3 className="m-0 text-[16px] font-bold">{title}</h3>
      {hint ? <p className="m-0 mt-1 text-[13px] text-text-muted">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function SourceStatus({ s }: { s: GigSource }) {
  if (s.missing.length)
    return (
      <span className="text-[13px]">
        <Badge tone="neutral">Off</Badge>
        <span className="mt-1 block text-[12px] text-text-muted">
          Needs{" "}
          {s.missing.map((k, i) => (
            <span key={k}>
              {i ? ", " : ""}
              <code className="rounded bg-surface-high px-1">{k}</code>
            </span>
          ))}{" "}
          in .env
        </span>
      </span>
    );
  if (!s.enabled) return <Badge tone="neutral">Switched off</Badge>;
  if (s.error)
    return (
      <span>
        <Badge tone="negative">Error</Badge>
        <span className="mt-1 block max-w-[260px] text-[12px] text-negative">{s.error}</span>
      </span>
    );
  return <Badge tone="positive">Working</Badge>;
}

/** Where the Gig Board's listings come from, the weekly reminders, partner feeds and gigs posted directly. */
export function GigSourcesScreen(props: GigSourcesScreenProps) {
  const { view, reminders, remindersError, canManage, refreshing, refreshingId } = props;

  const columns: Column<GigSource>[] = [
    {
      key: "name",
      header: "Source",
      sortValue: (s) => s.name.toLowerCase(),
      render: (s) => (
        <div className="min-w-0">
          <div className="font-semibold">
            {safeHref(s.site) ? (
              <a
                href={safeHref(s.site)}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent hover:underline"
              >
                {s.name}
              </a>
            ) : (
              s.name
            )}
          </div>
          {s.note ? <div className="text-[12px] text-text-muted">{s.note}</div> : null}
        </div>
      ),
    },
    {
      key: "kind",
      header: "Type",
      hideBelow: "md",
      sortValue: (s) => s.kind,
      render: (s) => s.kind,
    },
    { key: "status", header: "Status", render: (s) => <SourceStatus s={s} /> },
    {
      key: "live",
      header: "Listings now",
      align: "right",
      sortValue: (s) => s.live,
      render: (s) => <span className="tabular">{s.live}</span>,
    },
    {
      key: "lastRun",
      header: "Last run",
      hideBelow: "lg",
      sortValue: (s) => s.lastRun ?? "",
      render: (s) => <span className="whitespace-nowrap">{when(s.lastRun)}</span>,
    },
    {
      key: "lastOk",
      header: "Last updated",
      hideBelow: "sm",
      sortValue: (s) => s.lastOk ?? "",
      render: (s) => <span className="whitespace-nowrap">{when(s.lastOk)}</span>,
    },
    ...(canManage
      ? [
          {
            key: "refresh",
            header: "",
            align: "right" as const,
            render: (s: GigSource) => (
              <button
                type="button"
                className={ghost}
                disabled={refreshing || Boolean(s.missing.length) || !s.enabled}
                aria-label={`Refresh ${s.name}`}
                onClick={() => props.onRefresh(s.id)}
              >
                {refreshingId === s.id ? "Refreshing..." : "Refresh"}
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 max-w-[820px] text-[14px] text-text-muted">
        Where the Gig Board's listings come from. Sources refresh automatically several times a day.
        Listings are kept only if they match a skill track and people in Nigeria can apply.
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Listings on the board" value={view.totalListings} tone="accent" />
        <KpiCard
          label="Sources working"
          value={view.sources.filter((s) => s.enabled && !s.missing.length && !s.error).length}
          tone="positive"
          hint={`of ${view.sources.length}`}
        />
      </div>

      {refreshing ? (
        <Banner tone="note">
          {refreshingId && refreshingId !== "all"
            ? "Refreshing that source"
            : "Refreshing every source. This takes a minute or two"}
          ... This page updates by itself.
        </Banner>
      ) : null}
      {props.refreshError ? <Banner tone="bad">{props.refreshError}</Banner> : null}

      <DataTable
        rows={view.sources}
        columns={columns}
        rowKey={(s) => s.id}
        empty="No job sources are set up."
      />
      {canManage ? (
        <div>
          <button
            type="button"
            className={primary}
            disabled={refreshing}
            onClick={() => props.onRefresh()}
          >
            {refreshing && (refreshingId === "all" || !refreshingId)
              ? "Refreshing..."
              : "Refresh all sources now"}
          </button>
        </div>
      ) : null}

      <RemindersPanel
        reminders={reminders}
        error={remindersError}
        canManage={canManage}
        testing={props.testing}
        testResult={props.testResult}
        onTest={props.onTestReminder}
      />
      <FeedsPanel
        feeds={view.feeds}
        canManage={canManage}
        saving={props.saving === "feeds"}
        error={props.saveError?.list === "feeds" ? props.saveError.message : null}
        onSave={props.onSaveFeeds}
      />
      <PostsPanel
        posts={view.posts}
        canManage={canManage}
        saving={props.saving === "posts"}
        error={props.saveError?.list === "posts" ? props.saveError.message : null}
        onSave={props.onSavePosts}
      />
    </div>
  );
}

function RemindersPanel({
  reminders,
  error,
  canManage,
  testing,
  testResult,
  onTest,
}: {
  reminders: RemindersStatus | null;
  error: string | null;
  canManage: boolean;
  testing: boolean;
  testResult: { tone: "good" | "bad"; message: string } | null;
  onTest: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  if (error)
    return (
      <Panel title="Reminders">
        <Banner tone="bad">{error}</Banner>
      </Panel>
    );
  if (!reminders) return null;
  return (
    <Panel
      title="Reminders"
      hint={`Browser notifications participants turn on themselves: Monday mornings for new gigs (or their next step), Friday afternoons for the check-in, and a daily evening nudge for anyone not yet on the Gig Board who has gone a day without progress. ${reminders.schedule}`}
    >
      {reminders.enabled ? null : (
        <Banner tone="warn">Reminders are off: {reminders.reason}</Banner>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <KpiCard
          label="Participants with reminders on"
          value={reminders.people}
          tone="info"
          hint={`${reminders.devices} device${reminders.devices === 1 ? "" : "s"}`}
        />
        <div className="rounded-[14px] border border-border bg-surface p-4">
          <div className="text-[11.5px] font-bold uppercase tracking-[0.07em] text-text-muted">
            Last rounds
          </div>
          {reminders.rounds.length ? (
            <table className="mt-2 w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.07em] text-text-muted">
                  <th className="py-1 pr-2">Round</th>
                  <th className="py-1 pr-2">Started</th>
                  <th className="py-1 pr-2 text-right">Sent</th>
                  <th className="py-1 text-right">Not delivered</th>
                </tr>
              </thead>
              <tbody>
                {reminders.rounds.map((r) => (
                  <tr key={r.id} className="border-t border-[#efe3d2]">
                    <td className="py-1 pr-2">
                      {r.roundId}
                      {r.finished ? "" : " (sending)"}
                    </td>
                    <td className="py-1 pr-2 whitespace-nowrap">{when(r.started)}</td>
                    <td className="py-1 pr-2 text-right tabular">
                      {r.sent} of {r.total}
                    </td>
                    <td className="py-1 text-right tabular">{r.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="m-0 mt-2 text-[13px] text-text-muted">None sent yet.</p>
          )}
        </div>
      </div>
      {canManage && reminders.enabled ? (
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = email.trim().toLowerCase();
            if (value) onTest(value);
          }}
        >
          <label className="flex min-w-[240px] flex-1 flex-col gap-1 text-[13px] font-bold sm:max-w-[360px]">
            Send a test reminder to a participant
            <input
              type="email"
              className={inputClass}
              placeholder="their sign-in email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button type="submit" className={primary} disabled={testing || !email.trim()}>
            {testing ? "Sending..." : "Send test"}
          </button>
        </form>
      ) : null}
      {testResult ? <Banner tone={testResult.tone}>{testResult.message}</Banner> : null}
    </Panel>
  );
}

function TrackSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: BoardTrack;
  onChange: (v: BoardTrack) => void;
}) {
  return (
    <select
      id={id}
      className={inputClass}
      value={value}
      onChange={(e) => onChange(e.target.value as BoardTrack)}
    >
      {BOARD_TRACKS.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

function ListItem({
  title,
  detail,
  sub,
  onRemove,
  disabled,
}: {
  title: string;
  detail: string;
  sub: string;
  onRemove?: (() => void) | undefined;
  disabled?: boolean;
}) {
  return (
    <li className="flex items-center gap-3 rounded-[10px] border border-border p-3">
      <div className="min-w-0 flex-1">
        <div className="text-[14px]">
          <b>{title}</b> · {detail}
        </div>
        <div className="truncate text-[12.5px] text-text-muted">{sub}</div>
      </div>
      {onRemove ? (
        <button
          type="button"
          className={ghost}
          disabled={disabled}
          aria-label={`Remove ${title}`}
          onClick={onRemove}
        >
          Remove
        </button>
      ) : null}
    </li>
  );
}

const emptyFeed = (): PartnerFeed => ({ name: "", url: "", track: "uiux", openToNigeria: false });

function FeedsPanel({
  feeds,
  canManage,
  saving,
  error,
  onSave,
}: {
  feeds: PartnerFeed[];
  canManage: boolean;
  saving: boolean;
  error: string | null;
  onSave: (feeds: PartnerFeed[]) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<PartnerFeed>(emptyFeed);
  const [invalid, setInvalid] = useState<string | null>(null);
  const add = () => {
    const feed = { ...draft, name: draft.name.trim(), url: draft.url.trim() };
    if (!feed.name || !HTTPS.test(feed.url)) {
      setInvalid("A feed needs a name and a link starting with https://");
      return;
    }
    setInvalid(null);
    void onSave([...feeds, feed]).then((ok) => ok && setDraft(emptyFeed()));
  };
  return (
    <Panel
      title="Partner feeds"
      hint="Paste an RSS or JSON link a partner has given you (for example a Step 1 platform or a job board that agreed to share), and choose the skill track it belongs to."
    >
      {feeds.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {feeds.map((f, i) => (
            <ListItem
              key={`${f.url}-${i}`}
              title={f.name}
              detail={`${trackLabel(f.track)}${f.openToNigeria ? " · open to Nigeria" : ""}`}
              sub={f.url}
              disabled={saving}
              onRemove={canManage ? () => void onSave(feeds.filter((_, j) => j !== i)) : undefined}
            />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[13px] text-text-muted">No partner feeds yet.</p>
      )}
      {canManage ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-[13px] font-bold" htmlFor="fd-name">
              Name
              <input
                id="fd-name"
                className={inputClass}
                placeholder="e.g. Prodevs"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px] font-bold" htmlFor="fd-url">
              Feed link
              <input
                id="fd-url"
                type="url"
                className={inputClass}
                placeholder="https://..."
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px] font-bold" htmlFor="fd-track">
              Skill track
              <TrackSelect
                id="fd-track"
                value={draft.track}
                onChange={(track) => setDraft({ ...draft, track })}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-[13.5px]">
            <input
              type="checkbox"
              checked={draft.openToNigeria}
              onChange={(e) => setDraft({ ...draft, openToNigeria: e.target.checked })}
            />
            Every listing in this feed is open to people in Nigeria
          </label>
          <div>
            <button type="button" className={primary} disabled={saving} onClick={add}>
              {saving ? "Saving..." : "Add feed"}
            </button>
          </div>
        </div>
      ) : null}
      {invalid ? <Banner tone="bad">{invalid}</Banner> : null}
      {error ? <Banner tone="bad">{error}</Banner> : null}
    </Panel>
  );
}

const emptyPost = (): DirectPost => ({
  title: "",
  url: "",
  track: "uiux",
  company: "",
  pay: "",
  expires: "",
  desc: "",
});

function PostsPanel({
  posts,
  canManage,
  saving,
  error,
  onSave,
}: {
  posts: DirectPost[];
  canManage: boolean;
  saving: boolean;
  error: string | null;
  onSave: (posts: DirectPost[]) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<DirectPost>(emptyPost);
  const [invalid, setInvalid] = useState<string | null>(null);
  const set = (patch: Partial<DirectPost>) => setDraft({ ...draft, ...patch });
  const add = () => {
    const post: DirectPost = {
      ...draft,
      title: draft.title.trim(),
      url: draft.url.trim(),
      company: draft.company.trim(),
      pay: draft.pay.trim(),
      desc: draft.desc.trim(),
    };
    if (!post.title || !HTTPS.test(post.url)) {
      setInvalid("A gig needs a title and a link starting with https://");
      return;
    }
    if (post.expires && !/^\d{4}-\d{2}-\d{2}$/.test(post.expires)) {
      setInvalid("Check the closing date.");
      return;
    }
    setInvalid(null);
    void onSave([post, ...posts]).then((ok) => ok && setDraft(emptyPost()));
  };
  const field = (id: string, label: string, input: React.ReactNode) => (
    <label className="flex flex-col gap-1 text-[13px] font-bold" htmlFor={id}>
      {label}
      {input}
    </label>
  );
  return (
    <Panel
      title="Post a gig"
      hint={`Gigs from GATEWAY, CcHUB or employer partners, shown on the Gig Board marked "Posted by GATEWAY".`}
    >
      {posts.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {posts.map((p, i) => (
            <ListItem
              key={`${p.url}-${i}`}
              title={p.title}
              detail={`${trackLabel(p.track)}${p.company ? ` · ${p.company}` : ""}${p.expires ? ` · until ${p.expires}` : ""}`}
              sub={p.url}
              disabled={saving}
              onRemove={canManage ? () => void onSave(posts.filter((_, j) => j !== i)) : undefined}
            />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[13px] text-text-muted">No gigs posted yet.</p>
      )}
      {canManage ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field(
              "pg-title",
              "Title",
              <input
                id="pg-title"
                className={inputClass}
                placeholder="e.g. Social media designer for a fintech launch"
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
              />,
            )}
            {field(
              "pg-company",
              "Client or company",
              <input
                id="pg-company"
                className={inputClass}
                value={draft.company}
                onChange={(e) => set({ company: e.target.value })}
              />,
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {field(
              "pg-track",
              "Skill track",
              <TrackSelect
                id="pg-track"
                value={draft.track}
                onChange={(track) => set({ track })}
              />,
            )}
            {field(
              "pg-pay",
              "Pay (optional)",
              <input
                id="pg-pay"
                className={inputClass}
                placeholder="e.g. NGN 150,000"
                value={draft.pay}
                onChange={(e) => set({ pay: e.target.value })}
              />,
            )}
            {field(
              "pg-exp",
              "Show until",
              <input
                id="pg-exp"
                type="date"
                className={inputClass}
                value={draft.expires ?? ""}
                onChange={(e) => set({ expires: e.target.value })}
              />,
            )}
          </div>
          {field(
            "pg-url",
            "Link to apply",
            <input
              id="pg-url"
              type="url"
              className={inputClass}
              placeholder="https://..."
              value={draft.url}
              onChange={(e) => set({ url: e.target.value })}
            />,
          )}
          {field(
            "pg-desc",
            "Short description",
            <textarea
              id="pg-desc"
              rows={3}
              className={`${inputClass} h-auto py-2`}
              value={draft.desc}
              onChange={(e) => set({ desc: e.target.value })}
            />,
          )}
          <div>
            <button type="button" className={primary} disabled={saving} onClick={add}>
              {saving ? "Saving..." : "Post gig"}
            </button>
          </div>
        </div>
      ) : null}
      {invalid ? <Banner tone="bad">{invalid}</Banner> : null}
      {error ? <Banner tone="bad">{error}</Banner> : null}
    </Panel>
  );
}
